/**
 * Author: Gowtham
 *
 * Higher order component: withErrorHandler | Component: ErrorBoundaryComponent
 * Description:
 * - This higher-order component (HOC) and a React Component that wraps its children and catches JavaScript errors
 *   in the component tree below it. It provides a fallback UI when an error is encountered,
 *   preventing the entire application from crashing and allowing for graceful error handling.
 */

import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { type FC, type PropsWithChildren, useState } from 'react';
import getDisplayName from 'react-display-name';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';

import ErrorFallBackComponent from 'watchtower-ui/components/common/ErrorBoundary/ErrorFallBackComponent';

const withErrorHandler = <P extends object>(Component: FC<P>, passError?: boolean, Fallback?: FC<FallbackProps>) => {
  const ErrorFallBack = Fallback ?? ErrorFallBackComponent;

  const ComponentWithErrorHandling: FC<P> = (props) => {
    const [hasError, setHasError] = useState(false);
    return (
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary onReset={reset} FallbackComponent={ErrorFallBack} onError={() => setHasError(true)}>
            <Component {...props} {...(passError && { hasError })} />
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    );
  };

  ComponentWithErrorHandling.displayName = `WithErrorHandling${getDisplayName(Component as FC<unknown>)}`;

  return ComponentWithErrorHandling;
};

/* use this when we want to handle error at Component level */
const ErrorBoundaryComponent: FC<PropsWithChildren> = ({ children }) => (
  <ErrorBoundary FallbackComponent={ErrorFallBackComponent}>{children}</ErrorBoundary>
);

export { withErrorHandler, ErrorBoundaryComponent };
